import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { ChevronRight, WarningAmber } from '@mui/icons-material';
import { adminTokens as T } from '../../../theme/adminTokens';

export interface AttentionItem {
  id: string;
  label: string;
  count: number | null | undefined;
  helper?: string;
  tone?: 'neutral' | 'warning' | 'error' | 'info';
  onClick?: () => void;
}

export interface NeedsAttentionPanelProps {
  title?: string;
  items: AttentionItem[];
  loading?: boolean;
}

const toneToColors = (tone: AttentionItem['tone']) => {
  switch (tone) {
    case 'error':   return { bg: T.color.error.soft,   text: T.color.error.text,   dot: T.color.error.base };
    case 'warning': return { bg: T.color.warning.soft, text: T.color.warning.text, dot: T.color.warning.base };
    case 'info':    return { bg: T.color.info.soft,    text: T.color.info.text,    dot: T.color.info.base };
    default:        return { bg: T.color.brand[50],    text: T.color.brand[600],   dot: T.color.brand[400] };
  }
};

const NeedsAttentionPanel: React.FC<NeedsAttentionPanelProps> = ({
  title = 'Needs attention', items, loading,
}) => {
  return (
    <Box sx={{
      bgcolor: T.color.surface.raised,
      border: `1px solid ${T.color.surface.border}`,
      borderRadius: `${T.radius.lg}px`,
      p: `${T.space[5]}px`,
      boxShadow: T.shadow.sm,
      height: '100%',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px`, mb: `${T.space[4]}px` }}>
        <WarningAmber sx={{ fontSize: 20, color: T.color.warning.base }} />
        <Typography sx={{
          fontSize: T.type.size.lg,
          fontWeight: T.type.weight.semibold,
          color: T.color.text.primary,
        }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
        {loading ? (
          [0, 1, 2].map(i => <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: `${T.radius.md}px` }} />)
        ) : items.length === 0 ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            py: `${T.space[5]}px`, color: T.color.text.tertiary, fontSize: T.type.size.sm,
          }}>
            All clear — nothing needs attention right now.
          </Box>
        ) : (
          items.map(item => {
            const colors = toneToColors(item.tone);
            const interactive = !!item.onClick;
            return (
              <Box
                key={item.id}
                onClick={item.onClick}
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.onClick!(); } } : undefined}
                sx={{
                  display: 'flex', alignItems: 'center', gap: `${T.space[3]}px`,
                  p: `${T.space[3]}px`,
                  borderRadius: `${T.radius.md}px`,
                  bgcolor: colors.bg,
                  cursor: interactive ? 'pointer' : 'default',
                  transition: `transform ${T.motion.fast}, box-shadow ${T.motion.fast}`,
                  '&:hover': interactive ? { transform: 'translateY(-1px)', boxShadow: T.shadow.sm } : {},
                  '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 2px ${T.color.brand[200]}` },
                }}
              >
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: colors.dot, flexShrink: 0,
                }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: T.type.size.sm,
                    fontWeight: T.type.weight.semibold,
                    color: T.color.text.primary,
                  }}>
                    {item.label}
                  </Typography>
                  {item.helper && (
                    <Typography sx={{
                      fontSize: T.type.size.xs,
                      color: T.color.text.secondary,
                    }}>
                      {item.helper}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{
                  fontSize: T.type.size.xl,
                  fontWeight: T.type.weight.bold,
                  color: colors.text,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {item.count ?? '—'}
                </Typography>
                {interactive && <ChevronRight sx={{ fontSize: 18, color: T.color.text.tertiary }} />}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default NeedsAttentionPanel;
